import { extendObservable, action, computed, toJS } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import { isRequired, minLength, isValidEmail } from '../../../core/validations';
import { browserHistory } from 'react-router';
import jsonFormData from 'json-form-data';
import { CONTRACT_PRODUCTS } from '../../../core/consts';

class CheckoutLinkFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();
  productForm = new FormService();

  constructor() {
    extendObservable(this, {
      totalPrice: computed(() => {
        const products = this.form.getValue('products') || [];
        return products
          .filter(
            (p) =>
              ![
                'extra-license',
                'extra-license-kids',
                'placement-test',
                'extra-license-seat',
                'extra-license-range',
                'extra-license-seat-range',
              ].some((sku) => p.hs_sku === sku) && p.isEnabled
          )
          .reduce((acc, p) => {
            const price = !!p.amount ? p.amount * p.unitPrice : 0;
            if (CONTRACT_PRODUCTS.CUSTOM_PACKAGE === p.hs_sku) {
              return acc + (p.discount ? price - price * (p.discount / 100) : price);
            }
            return acc + price;
          }, 0);
      }),
    });
    this.form.validations = {
      customer: [isRequired],
      email: [isRequired, isValidEmail],
      hubspotDealId: [isRequired],
      segment: [isRequired],
      country: [isRequired],
      products: [isRequired, minLength(1)],
    };
    this.productForm.validations = {
      hs_sku: [isRequired],
      unitPrice: [isRequired],
    };
  }

  init = action(() => {
    this.fetch
      .fetch({
        url: `/countries`,
      })
      .then(() => {
        if (this.fetch.data) {
          this.form.setInitialValues({
            products: [],
            allowNewDashboard: 'true',
            country: this.fetch.data.find((c) => c.name.toLowerCase() === 'brazil').id,
          });
          this.form.reset();
        }
        this.fetch
          .fetch({
            url: `/checkout-links/products`,
            apiVersion: 'v3',
          })
          .then(() => {
            if (this.fetch.data) {
              const products = [...toJS(this.fetch.data)]
                .filter((product) => product.hs_sku !== CONTRACT_PRODUCTS.OTHERS)
                .map((product) => ({
                  ...product,
                  isEnabled: true,
                }))
                .sort((a, b) => a.order - b.order);
              this.form.setValue('products', products);
            }
          });
      })
      .catch(() => {
        this.productForm.setInitialValues({
          products: [],
          allowNewDashboard: 'true',
        });
        this.form.reset();
      });
  });

  handleSubmit = action(() => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }

    const props = {
      ...this.form.getValues(),
      products: this.form.getValue('products').filter((p) => p.isEnabled),
    };
    const formData = jsonFormData(props, {
      initialFormData: new FormData(),
    });

    this.submit
      .fetch({
        method: 'post',
        url: '/checkout-links',
        apiVersion: 'v3',
        body: formData,
      })
      .then(() => {
        if (this.submit.data) {
          this.form.reset();
          this.productForm.reset();
          this.form.setInitialValues({});
          this.productForm.setInitialValues({});

          browserHistory.goBack();

          NotificationService.addNotification(`Checkout link successfully.`, 'success');
        }
        if (this.submit.error) {
          NotificationService.addNotification(this.submit.error, 'error');
        }
      });
  });

  handleSubmitProduct = action(() => {
    this.productForm.submitted = true;
    if (this.productForm.errors) {
      return;
    }

    if (!this.productForm.getValue('amount')) {
      this.productForm.setValue('amount', null);
    }
    if (!this.productForm.getValue('freeAmount')) {
      this.productForm.setValue('freeAmount', null);
    }

    const isNew = !this.form.getValue('products').some((p) => p.hs_sku === this.productForm.getValue('hs_sku'));
    if (isNew) {
      this.form.setValue('products', [...(this.form.getValue('products') || []), this.productForm.getValues()]);
    } else {
      this.form.setValue(
        'products',
        [
          ...this.form.getValue('products').filter((p) => p.hs_sku !== this.productForm.getValue('hs_sku')),
          this.productForm.getValues(),
        ].sort((a, b) => a.order - b.order)
      );
    }

    this.productForm.reset();
    this.productForm.setInitialValues({});
    this.isModalOpen = false;
  });

  handleEditProduct = action((hs_sku) => {
    this.productForm.reset();
    this.productForm.setValues(this.form.getValue('products').find((p) => p.hs_sku === hs_sku));
    this.isModalOpen = true;
  });

  handleDeleteProduct = action((hs_sku) => {
    this.form.setValue(
      'products',
      this.form.getValue('products').filter((p) => p.hs_sku !== hs_sku)
    );
  });

  handleToggleProduct = action((hs_sku) => {
    this.form.setValue(
      'products',
      this.form.getValue('products').map((p) => (p.hs_sku === hs_sku ? { ...p, isEnabled: !p.isEnabled } : p))
    );
  });

  handleCloseModal = action(() => {
    this.productForm.reset();
    this.isModalOpen = false;
  });
}

const checkoutLinkFormService = new CheckoutLinkFormService();

export default checkoutLinkFormService;
